import React, { useContext, useState, useEffect } from 'react';

import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';

import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

import CallIcon from '@material-ui/icons/Call';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import TimerIcon from '@material-ui/icons/Timer';

import { makeStyles } from '@material-ui/core/styles';
import { grey, blue } from '@material-ui/core/colors';
import { toast } from 'react-toastify';

// import useDashboard from '../../hooks/useDashboard';
import { ChatsUser } from './ChartsUser';

import { ChartsDate } from './ChartsDate';
import { i18n } from '../../translate/i18n';

// Imports para métricas
import MetricsCards from '../../components/MetricsCards';
import MetricsCharts from '../../components/MetricsCharts';
import MetricsTables from '../../components/MetricsTables';
import MetricsFilters from '../../components/MetricsFilters';
// import GoalsManager from '../../components/GoalsManager';
import { useMetricsContext } from '../../context/Metrics/MetricsContext';
// import { useUserGoals } from '../../hooks/useUserGoals';
import { AuthContext } from '../../context/Auth/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import Title from '../../components/Title';

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(2),
  },
  fixedHeightPaper: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: 240,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
  },
  cardAvatar: {
    fontSize: '55px',
    color: grey[500],
    backgroundColor: '#ffffff',
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
  cardTitle: {
    fontSize: '18px',
    color: blue[700],
  },
  cardSubtitle: {
    color: grey[600],
    fontSize: '14px',
  },

  iframeDashboard: {
    width: '100%',
    height: 'calc(100vh - 64px)',
    border: 'none',
  },
  customFixedHeightPaper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: 120,
  },
  customFixedHeightPaperLg: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: '100%',
  },
  metricCard: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: '100%',
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.boxticket.main
        : theme.palette.primary.main,
    color: '#eee',
  },
  fixedHeightPaper2: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
  },
  metricsSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionSubtitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  chartsSection: {
    marginTop: theme.spacing(3),
  },
  tablesSection: {
    marginTop: theme.spacing(3),
  },
  chartsDateSection: {
    marginTop: theme.spacing(3),
  },
  chartsUserSection: {
    marginTop: theme.spacing(3),
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { permissions } = usePermissions();

  // Verificar se é admin usando permissões
  const isAdmin = permissions?.isAdmin || user?.profile === 'admin';

  const {
    metrics,
    realtimeMetrics,
    loading: metricsLoading,
    realtimeLoading,
    filters,
    updateFilters,
    resetFilters,
  } = useMetricsContext();
  // const {
  //   goals,
  //   loading: goalsLoading,
  //   createGoal,
  //   updateGoal,
  //   deleteGoal,
  // } = useUserGoals(isAdmin ? null : user?.id);

  function formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}h ${mins.toString().padStart(2, '0')}m`;
  }

  return (
    <div>
      <Container maxWidth='lg' className={classes.container}>
        {/* TÍTULO E SUBTÍTULO */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={12}>
            <Title>{i18n.t('metrics.title')}</Title>
            <Typography variant='body1' className={classes.sectionSubtitle}>
              {i18n.t('metrics.subtitle')}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent='flex-end'>
          {/* CARDS PRINCIPAIS - MÉTRICAS GERAIS */}

          {/* Em conversa */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.inTalk')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {realtimeLoading
                        ? '...'
                        : realtimeMetrics?.openTickets || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={2}>
                  <CallIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Aguardando */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.waiting')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {metricsLoading ? '...' : metrics?.pendingTickets || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <HourglassEmptyIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Finalizados */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.finished')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {realtimeLoading
                        ? '...'
                        : realtimeMetrics?.closedTickets || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <CheckCircleIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Novos contatos */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.newContacts')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {realtimeLoading
                        ? '...'
                        : realtimeMetrics?.newContacts || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <GroupAddIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Tempo medio de conversa */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.averageTalkTime')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {realtimeLoading
                        ? '...'
                        : formatTime(
                            realtimeMetrics?.averageConversionTime || 0
                          )}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <AccessAlarmIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Tempo medo de resposta */}
          <Grid item xs={12} sm={6} md={4}>
            <Paper
              className={classes.metricCard}
              style={{ overflow: 'hidden' }}
              elevation={2}
            >
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography component='h3' variant='h6' paragraph>
                    {i18n.t('dashboard.counters.averageResponseTime')}
                  </Typography>
                  <Grid item>
                    <Typography component='h1' variant='h4'>
                      {realtimeLoading
                        ? '...'
                        : formatTime(realtimeMetrics?.averageResponseTime || 0)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <TimerIcon
                    style={{
                      fontSize: 100,
                      color: '#FFFFFF',
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* SEÇÃO UNIFICADA DE MÉTRICAS */}
          <Grid item xs={12} className={classes.metricsSection}>
            {/* FILTROS DE MÉTRICAS  */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MetricsFilters
                  filters={filters}
                  onFiltersChange={updateFilters}
                  onReset={resetFilters}
                  onApply={() => {}}
                />
              </Grid>
            </Grid>

            {/* CARDS DE MÉTRICAS */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MetricsCards
                  metrics={metrics}
                  // goals={goals}
                  realtimeMetrics={realtimeMetrics}
                />
              </Grid>
            </Grid>

            {/* GRÁFICOS DE MÉTRICAS */}
            <Grid container spacing={2} className={classes.chartsSection}>
              <Grid item xs={12}>
                <MetricsCharts metrics={metrics} isAdmin={isAdmin} />
              </Grid>
            </Grid>

            {/* TABELAS DE MÉTRICAS */}
            <Grid container spacing={2} className={classes.tablesSection}>
              <Grid item xs={12}>
                <MetricsTables metrics={metrics} isAdmin={isAdmin} />
              </Grid>
            </Grid>

            {/* GERENCIADOR DE METAS (APENAS ADMIN) - COMENTADO */}
            {/* {isAdmin && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <GoalsManager />
                </Grid>
              </Grid>
            )} */}

            {/* GRÁFICOS DE ATENDIMENTOS */}
            {isAdmin && (
              <Grid container spacing={2} className={classes.chartsUserSection}>
                <Grid item xs={12}>
                  <Paper className={classes.fixedHeightPaper2}>
                    <ChatsUser />
                  </Paper>
                </Grid>
              </Grid>
            )}

            <Grid container spacing={2} className={classes.chartsDateSection}>
              <Grid item xs={12}>
                <Paper className={classes.fixedHeightPaper2}>
                  <ChartsDate />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </div>
  );
};

export default Dashboard;
